import { ACCOUNT_DEACTIVATED_MESSAGE } from "@pcj/shared";
import { useQueryClient } from "@tanstack/react-query";
import { createAuthClient } from "better-auth/react";
import { useNavigate } from "react-router";

/** Better Auth client: same origin, endpoints under /api/auth. */
export const authClient = createAuthClient({ basePath: "/api/auth" });

/** Ends the session, forgets every cached query and goes to the login page. */
export function useSignOut() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return async () => {
    await authClient.signOut();
    queryClient.clear();
    navigate("/login", { replace: true });
  };
}

// Better Auth answers with English codes; users read Spanish.
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  INVALID_EMAIL_OR_PASSWORD: "Correo o contraseña incorrectos.",
  EMAIL_NOT_VERIFIED: "Primero confirma tu correo. Te enviamos un nuevo enlace de confirmación.",
  USER_ALREADY_EXISTS: "Ya existe una cuenta con ese correo.",
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: "Ya existe una cuenta con ese correo.",
  PASSWORD_TOO_SHORT: "La contraseña debe tener al menos 10 caracteres.",
  PASSWORD_TOO_LONG: "La contraseña es demasiado larga.",
  INVALID_EMAIL: "El correo electrónico no es válido.",
  INVALID_TOKEN: "El enlace no es válido o ya venció. Solicita uno nuevo.",
  TOKEN_EXPIRED: "El enlace ya venció. Solicita uno nuevo.",
  USER_NOT_FOUND: "No encontramos una cuenta con ese correo.",
  // Raised by the API's session hook (apps/api/src/auth.ts).
  ACCOUNT_DEACTIVATED: ACCOUNT_DEACTIVATED_MESSAGE,
};

export function authErrorMessage(error: { code?: string; status?: number; message?: string } | null): string {
  if (!error) return "";
  if (error.status === 429) return "Demasiados intentos. Espera un minuto e intenta de nuevo.";
  return (error.code && AUTH_ERROR_MESSAGES[error.code]) || "No pudimos completar la solicitud. Intenta de nuevo.";
}
